import { motion } from "framer-motion";
import { MessageCircle, Calendar } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const festivals = [
  {
    n: "01",
    name: "Durga Pooja",
    hindi: "दुर्गा पूजा",
    date: "Oct 11 – 19, 2026",
    tag: "Sharad's Grandest Celebration",
    desc: "When the dhak beats and the pandal glows, Maa Durga arrives among marigolds. Bring home handcrafted murtis, pure pooja ghee and diyas for your pandal and your home shrine.",
    img: "/assets/durga.jpg",
    alt: "Traditional Durga murti adorned in red and gold with marigold garlands",
    cta: "Order for Durga Pooja",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20for%20Durga%20Pooja.",
    bg: "bg-[#FBEAE4]",
    accent: "text-[#9C2B1B]",
    btn: "bg-[#9C2B1B] hover:bg-brand-gold",
  },
  {
    n: "02",
    name: "Karwa Chauth Specials",
    hindi: "करवा चौथ",
    date: "Oct 29, 2026",
    tag: "Suhagon ka Tyohaar",
    desc: "The day the var and the vadhu bless and worship each other — celebrating their love and marriage with a promise of a lifetime together. For every wedded couple, the pooja feels incomplete without the mandala pooja at its heart. Our pan-shaped mandala photo frame is handcrafted for that sacred moment — and makes a cherished gift for your favourite couple.",
    img: "/assets/mandala-frame.jpg",
    alt: "Pan-shaped mandala photo frame with red and gold mandala art, pearl tassels, bangles and a lit diya",
    cta: "Order Your Mandala Frame",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20a%20mandala%20photo%20frame%20for%20Karwa%20Chauth.",
    bg: "bg-[#FBE9F1]",
    accent: "text-[#A81C58]",
    btn: "bg-[#A81C58] hover:bg-brand-gold",
  },
  {
    n: "03",
    name: "Dhanteras",
    hindi: "धनतेरस",
    date: "Nov 6, 2026",
    tag: "Where Prosperity Meets Maryada",
    desc: "Shri Ram represents maryada — integrity, duty and right conduct. His murti reminds us to earn with honesty and use with wisdom. This Dhanteras, bring home His chavi: a quiet commitment to prayer, compassion and family harmony.",
    verse: "इस धनतेरस, श्रीराम का स्वागत करें — धर्म, मर्यादा और भक्ति के साथ शुभ शुरुआत करें।",
    img: "/assets/ram-murti.jpg",
    alt: "Single murti of Shri Ram with golden bow, tulsi garland and lit brass diyas",
    cta: "Order Shri Ram Murti",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20the%20Shri%20Ram%20murti%20for%20Dhanteras.",
    bg: "bg-[#EDF4EC]",
    accent: "text-[#2F6B3A]",
    btn: "bg-[#2F6B3A] hover:bg-brand-gold",
  },
  {
    n: "04",
    name: "Maa Lakshmi",
    hindi: "माँ लक्ष्मी",
    date: "Nov 6, 2026 · Dhanteras Night",
    tag: "The Goddess of Abundance",
    desc: "Dhanteras night belongs to Maa Lakshmi — the goddess of wealth, grace and abundance. Welcome Her home with a handcrafted chavi, pure ghee diyas and a heart swept clean; where She is received with devotion, prosperity stays.",
    img: "/assets/lakshmi-murti.jpg",
    alt: "Maa Lakshmi murti seated on a pink lotus with gold coins and lit diyas",
    cta: "Order Lakshmi Murti",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20the%20Maa%20Lakshmi%20murti%20for%20Dhanteras.",
    bg: "bg-[#F6E7E9]",
    accent: "text-[#8C2F4E]",
    btn: "bg-[#8C2F4E] hover:bg-brand-gold",
  },
  {
    n: "05",
    name: "Lord Kuber",
    hindi: "कुबेर देव",
    date: "Nov 6, 2026 · Dhanteras",
    tag: "The Guardian of Wealth",
    desc: "Lord Kuber, the guardian of wealth, is remembered on Dhanteras — when homes are swept clean and treasures are blessed. Bring home His chavi beside Maa Lakshmi, and let fortune find an honest dwelling.",
    img: "/assets/kuber-murti.jpg",
    alt: "Brass Kuber murti with treasure pot, mongoose and gold coins among marigolds",
    cta: "Order Kuber Murti",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20the%20Lord%20Kuber%20murti%20for%20Dhanteras.",
    bg: "bg-[#F3ECDC]",
    accent: "text-[#7A5B16]",
    btn: "bg-[#7A5B16] hover:bg-brand-gold",
  },
  {
    n: "06",
    name: "Diwali Specials",
    hindi: "दिवाली",
    date: "Nov 8, 2026",
    tag: "The Festival of Lights",
    desc: "Rangoli-ready diyas, slow-churned pooja ghee and our blessed Ram Parivar — Ram, Sita, Lakshman and Hanuman — waiting to light up your home temple this Diwali.",
    img: "/assets/diwali.jpg",
    alt: "Ram Parivar idols beside a colourful rangoli surrounded by lit diyas",
    cta: "Order Your Ram Parivar",
    wa: "Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20the%20Ram%20Parivar%20for%20Diwali.",
    bg: "bg-[#FFF3DC]",
    accent: "text-[#B06A00]",
    btn: "bg-[#B06A00] hover:bg-brand-gold",
  },
];

const FestivalBanner = ({ f, i }) => {
  const flip = i % 2 === 1;
  return (
    <section className={`${f.bg} py-20 lg:py-28`} data-testid={`festival-${f.n}`}>
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-12 md:gap-16 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: EASE }}
          className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}
        >
          <div className="arch-frame border border-white/70 p-3 shadow-sm">
            <div className="arch-frame overflow-hidden">
              <img src={f.img} alt={f.alt} loading="lazy" data-testid={`festival-image-${f.n}`} className="aspect-[4/5] w-full object-cover" />
            </div>
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.12, ease: EASE }}
          className={`md:col-span-6 ${flip ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}
        >
          <p className={`text-[11px] font-medium uppercase tracking-[0.35em] ${f.accent}`}>{f.tag}</p>
          <h2 className="mt-4 font-display text-5xl font-medium text-brand-ink md:text-6xl">{f.name}</h2>
          <p className={`mt-2 font-display text-2xl italic ${f.accent}`}>{f.hindi}</p>
          <p className={`mt-5 inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] ${f.accent}`} data-testid={`festival-date-${f.n}`}>
            <Calendar className="h-3 w-3" strokeWidth={1.5} />
            {f.date} · Order Early
          </p>
          <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-brand-ink/70">{f.desc}</p>
          {f.verse && (
            <p className={`mt-6 max-w-lg font-display text-xl italic leading-snug md:text-2xl ${f.accent}`} data-testid={`festival-verse-${f.n}`}>
              “{f.verse}”
            </p>
          )}
          <a
            href={`https://wa.me/919205511502?text=${f.wa}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`festival-order-${f.n}`}
            className={`group mt-10 inline-flex items-center gap-3 rounded-full px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-white transition-colors duration-300 ${f.btn}`}
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
            {f.cta}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default function Festivals() {
  return (
    <main data-testid="festivals-page" className="pt-32 lg:pt-40">
      <div className="mx-auto max-w-7xl px-6 pb-20 text-center lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold"
        >
          Festive Specials · उत्सव
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mx-auto mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-brand-ink md:text-6xl"
        >
          Celebrate every festival <em className="italic text-brand-maroon">the traditional way.</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
          className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-brand-ink/70"
        >
          Handcrafted offerings for the days that matter most — made in small
          batches, in season, with devotion. Order early; the festivals wait for no one.
        </motion.p>
      </div>
      {festivals.map((f, i) => (
        <FestivalBanner key={f.n} f={f} i={i} />
      ))}
    </main>
  );
}
